import { useContext, useMemo } from "react";
import { tableHooks } from "components/Table";
import { AssessmentCategoryCell, ProsodyCell, RateAndAccuracyCell, RateAndAccuracyHeaderCell, RateAndAccuracyLabelCell, RateAndAccuracyValueCell, ReadingComprehensionCell, ShortGradeCell, StatusCell } from "..";
import { percent, timeAgoWithOutSeconds } from "utils/formatter";
import { AssessmentsContext } from "../AssessmentsContext";
import { IconButtonGroup } from "components/Form";
import { ReactComponent as VisibilityDisabledIcon } from 'assets/icons/VisibilityDisabled.svg';
import { ReactComponent as VisibilityIcon } from 'assets/icons/Visibility.svg';
import { useQueryClient } from "@tanstack/react-query";
import { queryClient as qc, statuses } from 'keys'
import { filter, map } from "lodash";
import { actions } from "store";
import { useDispatch } from "react-redux";
import { app } from "router/routes";
import { useNavigate } from "react-router-dom";
import { ratingListLabel3 } from "keys/rating";
import { FactoredPercentAccuracyTooltip } from "pages/School/Report";
import { IconButton, SvgIcon, Tooltip } from "@mui/material";
import FluencyTooltip from "pages/School/Report/FluencyTooltip";

const useColumns = () => {
    const idCell = tableHooks.useIdCell()
    const testTakerNameCell = tableHooks.useTestTakerNameCell()
    const gradeOfAllocationCell = tableHooks.useGradeOfAllocationCell()
    const playCell = tableHooks.usePlayCell()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { viewCell, setViewReportId } = useContext(AssessmentsContext)
    const queryClient = useQueryClient()

    const viewCellComponents = {
        icon: RateAndAccuracyCell,
        label: RateAndAccuracyLabelCell,
        value: RateAndAccuracyValueCell
    }

    const goAssessment = (item) => {
        const { id } = item
        const assessments = queryClient.getQueryData([qc.report])?.data
        const assessmentIds = map(filter(assessments, { status: statuses.inProgress, sub_status: statuses.wordsComplete }), 'id');
        dispatch(actions.setAssessmentIds(assessmentIds))
        setViewReportId(id)
    }

    const columns = useMemo(
        () => [
            idCell,
            {
                accessorKey: 'reading_assessment_title',
                header: "שם מבדק",
            },
            {
                accessorKey: 'reading_assessment_def_id',
                header: "ID מבדק",
                feature: "reading_assessment_def_id__cell",
                role: "read"
            },
            {
                accessorKey: 'reading_assessment_category_label',
                header: "סוג מבדק",
                cell: AssessmentCategoryCell
            },
            {
                accessorKey: 'reading_assessment_activated_by',
                header: `הופעל ע"י`,
                feature: "activated-by-cell",
                role: "read",
                showBrokenDisplay: true
            },
            {
                accessorKey: 'sub_status',
                header: "סטטוס תוצאות",
                cell: StatusCell,
                enableSorting: false
            },
            {
                accessorKey: 'file_uploaded_date',
                header: "מועד ביצוע",
                cell: info => <div style={{ width: 140 }}>{timeAgoWithOutSeconds(info.getValue())}</div>
            },
            {
                accessorKey: 'calculations.words_correct_per_minute',
                header: RateAndAccuracyHeaderCell,
                alignCenter: true,
                cell: viewCellComponents[viewCell["calculations.words_correct_per_minute"]],
                meta: {
                    label: "מילים נכונות לדקה",
                    field: "correct_words_per_minute",
                    index: "correct_words_per_minute_rating_index",
                    percent: false
                }
            },
            {
                accessorKey: 'calculations.words_per_minute',
                header: RateAndAccuracyHeaderCell,
                alignCenter: true,
                cell: viewCellComponents[viewCell["calculations.words_per_minute"]],
                meta: {
                    field: "num_words_per_minute",
                    id: "words_per_minute_rating_id",
                    index: "words_per_minute_rating_index",
                    label: "קצב"
                }
            },
            {
                accessorKey: 'calculations.factored_percent_accuracy',
                header: RateAndAccuracyHeaderCell,
                alignCenter: true,
                cell: viewCellComponents[viewCell["calculations.factored_percent_accuracy"]],
                meta: {
                    field: "factored_percent_accuracy",
                    id: "factored_percent_accuracy_rating_id",
                    index: "factored_percent_accuracy_rating_index",
                    label: "דיוק (משוקלל)",
                    percent: true,
                    tooltip: FactoredPercentAccuracyTooltip
                }
            },
            {
                accessorKey: 'calculations.percent_accuracy',
                header: RateAndAccuracyHeaderCell,
                alignCenter: true,
                cell: viewCellComponents[viewCell["calculations.percent_accuracy"]],
                meta: {
                    field: "percent_accuracy",
                    id: "percent_accuracy_rating_id",
                    index: "percent_accuracy_rating_index",
                    label: "דיוק (גוֺלְמִי)",
                    percent: true
                }
            },
            {
                accessorKey: 'calculations.fluency_reading',
                header: "רצף",
                header: RateAndAccuracyHeaderCell,
                alignCenter: true,
                cell: viewCellComponents[viewCell["calculations.fluency_reading"]],
                meta: {
                    field: "fluency_reading",
                    index: "fluency_reading_rating_index",
                    percent: true,
                    label: "רצף",
                    ratingLabels: ratingListLabel3,
                    tooltip: FluencyTooltip
                }
            },
            {
                accessorKey: 'prosody',
                header: " פרוזודיה",
                cell: ProsodyCell,
                enableSorting: false
            },
            {
                accessorKey: "multiple_choice_passage_id",
                header: "הבנת הנקרא",
                alignCenter: true,
                enableSorting: false,
                cell: ReadingComprehensionCell
            },
            {
                accessorKey: 'rating.error_percent',
                header: " טעויות",
                cell: info => percent(info.getValue())
            },
            {
                accessorKey: 'rating.repetition_percent',
                header: " חזרות",
                cell: info => percent(info.getValue())
            },
            {
                accessorKey: 'rating.insertion_percent',
                header: "הוספות",
                cell: info => percent(info.getValue()),
            },
            {
                accessorKey: 'rating.omission_error_and_minor_error_percent',
                header: "השמטות",
                // enableSorting: false,
                cell: info => percent(info.getValue()),
            },
            {
                accessorKey: 'rating.self_correction_percent',
                header: " תקון עצמי",
                cell: info => percent(info.getValue()),
            },
            {
                accessorKey: 'rating.minor_error_percent',
                header: " שגיאה קלה",
                cell: info => percent(info.getValue()),
            },
            {
                accessorKey: 'rating.self_correction_and_minor_error_percent',
                header: " שגיאה קלה עם תקון עצמי",
                cell: info => percent(info.getValue()),
            },
            playCell,
            {
                accessorKey: 'reports',
                header: "צפיה בדו”ח",
                enableSorting: false,
                cell: ({ row }) => {
                    const { rating, id } = row.original || {}

                    const disabled = !rating?.percent_accuracy_rating_id || !rating?.words_per_minute_rating_id

                    return [
                        <Tooltip key={row.id} tooltip="מעבר לתצוגה מילולית" placement="left">
                            <IconButton
                                className="transparent-icon"
                                disabled={disabled}
                                onClick={() => {
                                    goAssessment({ ...row.original, id: row.original.id })
                                }}
                            >
                                <SvgIcon component={disabled ? VisibilityDisabledIcon : VisibilityIcon} inheritViewBox />
                            </IconButton>
                        </Tooltip>
                        // <IconButtonGroup
                        //     color="thirdly4"
                        //     key={row.id}
                        //     tooltip="מעבר לתצוגה מילולית"
                        //     icon={disabled ? VisibilityDisabledIcon : VisibilityIcon}
                        //     selected={!disabled}
                        //     disabled={disabled}
                        //     action={() => {
                        //         goAssessment({ ...row.original, id: row.original.id })
                        //     }}
                        // />
                    ];
                }
            }
        ], [viewCell]
    )

    return columns
}

export default useColumns